import { useState } from 'react'
import { AdvertisingCard } from '@/widgets/advertising-card'
import { Header } from '@/widgets/header'
import { MobileTabBar, mobileTabBarIcons } from '@/widgets/mobile-tab-bar'
import { PartnerOffersCarousel } from '@/widgets/partner-offers'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import {
  ContentGroup,
  ArrowLeftIcon,
  ChevronRightIcon,
  CouponIcon,
  MessageSquareIcon,
  PlayCircleIcon,
} from '@/shared/ui'
import { partnerOffers } from '@/shared/content/partner-offers'
import { routes } from '@/shared/config'
import giftBannerPaper from '@/shared/assets/dashboard/lk/gift-banner-paper.png'

type DashboardLkMobileProps = {
  className?: string
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const subtitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.base),
}

const bannerCtaStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.advertising.title),
}

const partnerTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
  color: designTokens.colors.text.primary,
}

const isGiftBannerEnabled = true

const renderPlayIcon = ({
  width,
  height,
  color,
  strokeWidth,
}: {
  width: string
  height: string
  color: string
  strokeWidth: string
}) => (
  <PlayCircleIcon
    width={width}
    height={height}
    color={color}
    strokeWidth={Number.parseFloat(strokeWidth)}
  />
)

const renderMessageIcon = ({
  width,
  height,
  color,
}: {
  width: string
  height: string
  color: string
  strokeWidth: string
}) => <MessageSquareIcon width={width} height={height} color={color} />

const renderCouponIcon = ({
  width,
  height,
  color,
}: {
  width: string
  height: string
  color: string
  strokeWidth: string
}) => <CouponIcon width={width} height={height} color={color} />

export function DashboardLkMobile({ className }: DashboardLkMobileProps) {
  const [showPartnerDiscounts, setShowPartnerDiscounts] = useState(false)

  return (
    <div className={cn('flex w-full flex-col lg:hidden', className)}>
      <div
        className="flex w-full flex-col"
        style={{ paddingBottom: designTokens.spacing.popupModalMobileBottomOffset }}
      >
        <Header className="flex" />
        {showPartnerDiscounts ? (
          <ContentGroup variant="planSelectorMobile" className="w-full">
            <div className="flex w-full items-center" style={{ gap: designTokens.spacing.xxxl }}>
              <button type="button" onClick={() => setShowPartnerDiscounts(false)}>
                <ArrowLeftIcon
                  style={{ color: designTokens.colors.text.primary }}
                  width={designTokens.components.account.mobile.headerIconSize}
                  height={designTokens.components.account.mobile.headerIconSize}
                />
              </button>
              <p style={titleStyle}>Партнерские скидки</p>
            </div>
            <div
              className="flex w-full flex-col"
              style={{
                background: designTokens.colors.background.glassMedium,
                borderRadius: designTokens.borders.radius.lg,
                padding: `${designTokens.spacing.lg} ${designTokens.spacing.md}`,
                gap: designTokens.spacing.lg,
              }}
            >
              <div className="flex items-center" style={{ gap: designTokens.spacing.md }}>
                {renderCouponIcon({
                  width: designTokens.sizes.width.advertisingCardIconMobile,
                  height: designTokens.sizes.height.advertisingCardIconMobile,
                  color: designTokens.colors.text.primary,
                  strokeWidth: designTokens.borders.widths.advertisingCardIconMobile,
                })}
                <p style={partnerTitleStyle}>Партнерские скидки</p>
              </div>
              <PartnerOffersCarousel variant="vertical" offers={partnerOffers} />
            </div>
          </ContentGroup>
        ) : (
          <div
            className="flex w-full flex-col"
            style={{ gap: designTokens.spacing.accountLkMobileStackGap }}
          >
            <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xs }}>
              <h1 style={titleStyle}>Доступно в подписке</h1>
              <p style={subtitleStyle}>Используйте все возможности комьюнити</p>
            </div>
            <div className="flex w-full" style={{ gap: designTokens.spacing.md }}>
              <AdvertisingCard
                variant="mobile"
                title="Видеоплатформа с кейсами"
                icon={renderPlayIcon}
                className="flex-1"
                style={{ width: designTokens.sizes.width.fill }}
              />
              <AdvertisingCard
                variant="mobile"
                title="Бот комьюнити и нетворкинг"
                icon={renderMessageIcon}
                className="flex-1"
                style={{ width: designTokens.sizes.width.fill }}
              />
            </div>
            {isGiftBannerEnabled ? (
              <div
                className="flex w-full flex-col items-center"
                style={{
                  background: designTokens.colors.brand.secondary,
                  borderRadius: designTokens.borders.radius.lg,
                  boxShadow: designTokens.shadows.mobileAdCard,
                  overflow: 'hidden',
                }}
              >
                <div className="relative w-full" style={{ padding: designTokens.spacing.md }}>
                  <div
                    className="relative"
                    style={{
                      borderRadius: designTokens.components.account.giftBanner.innerRadius,
                      border: `${designTokens.components.account.giftBanner.borderWidth} solid ${designTokens.colors.brand.secondary}`,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={giftBannerPaper}
                      alt=""
                      style={{
                        width: '100%',
                        height: designTokens.components.account.giftBanner.imageHeight,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                </div>
                <div
                  className="flex w-full items-center justify-between"
                  style={{ padding: designTokens.spacing.md }}
                >
                  <p style={bannerCtaStyle}>Сделать подарок</p>
                  <ChevronRightIcon
                    color={designTokens.colors.text.inverse}
                    width={designTokens.components.account.giftBanner.ctaIconSize}
                    height={designTokens.components.account.giftBanner.ctaIconSize}
                  />
                </div>
              </div>
            ) : null}
            <AdvertisingCard
              variant="mobile"
              title="Менторская доска"
              icon={renderPlayIcon}
              style={{ width: designTokens.sizes.width.fill }}
            />
            <button
              type="button"
              className="w-full text-left"
              onClick={() => setShowPartnerDiscounts(true)}
            >
              <AdvertisingCard
                variant="mobile"
                title="Партнерские скидки"
                icon={renderCouponIcon}
                style={{ width: designTokens.sizes.width.fill }}
              />
            </button>
          </div>
        )}
      </div>
      <MobileTabBar
        className="lg:hidden"
        activeKey="home"
        items={[
          { key: 'home', label: 'Главная', icon: mobileTabBarIcons.home, href: routes.dashboardLk },
          {
            key: 'subscription',
            label: 'Подписка',
            icon: mobileTabBarIcons.subscription,
            href: routes.dashboardSubscription,
          },
          {
            key: 'profile',
            label: 'Профиль',
            icon: mobileTabBarIcons.profile,
            href: routes.dashboardProfile,
          },
        ]}
      />
    </div>
  )
}

DashboardLkMobile.displayName = 'DashboardLkMobile'
